import {
   ReviewTemplate,
   ReviewTemplateOption,
   ReviewTemplateQuestion,
} from './components/review-assessment-sections/review-assessment-sections.component'
import { DVM_REVIEW_TEMPLATE } from './review-template-dvm'
import { HM_REVIEW_TEMPLATE } from './review-template-hm'
import { COS_REVIEW_TEMPLATE } from './review-template-cos'
import { DO_REVIEW_TEMPLATE } from './review-template-do'
import { RM_REVIEW_TEMPLATE } from './review-template-rm'
import { RECEPTION_REVIEW_TEMPLATE } from './review-template-reception'
import { CROSS_TRAINED_REVIEW_TEMPLATE } from './review-template-cross-trained'
import { CLINICAL_REVIEW_TEMPLATE } from './review-template-clinical'

// ─────────────────────────────────────────────────────────────────────────────
// Peer Input: a manager asks a colleague to fill out a blank copy of the
// manager's side of a review, then copies any of those answers into their own.
// Shared by the panel on the review page and the colleague's page.
// ─────────────────────────────────────────────────────────────────────────────

/** One answer, in the same shape the review already saves (ReviewResponses). */
export interface PeerInputResponse {
   questionId: string
   sectionId: string
   respondentType: string
   responseValue: string | null
   otherText: string | null
   notes: string | null
}

export interface PeerInputRequest {
   id: number
   revieweeUserId: number
   revieweeName: string
   reviewType: string
   practiceName: string
   requestedByUserId: number
   requestedByName: string
   peerUserId: number
   peerName: string
   status: 'Requested' | 'Submitted'
   requestedOn: string
   submittedOn: string | null
   updatedOn: string | null
   link: string
   alreadyRequested?: boolean
}

export interface PeerInputDetail extends PeerInputRequest {
   isPeer: boolean
   responses: PeerInputResponse[]
   additionalResponsibilities: any[]
}

export interface PeerCandidate {
   userId: number
   displayName: string
   jobTitle: string
   practiceName: string
   matchReason: string // 'Same hospital' | 'Same role'
}

/** Untouched templates. Only the reviewee side has job-description tokens, and peers fill out the manager side. */
const TEMPLATES: Record<string, ReviewTemplate> = {
   DVM: DVM_REVIEW_TEMPLATE,
   HM: HM_REVIEW_TEMPLATE,
   COS: COS_REVIEW_TEMPLATE,
   DO: DO_REVIEW_TEMPLATE,
   RM: RM_REVIEW_TEMPLATE,
   RECEPTION: RECEPTION_REVIEW_TEMPLATE,
   CROSSTRAINED: CROSS_TRAINED_REVIEW_TEMPLATE,
   CLINICAL: CLINICAL_REVIEW_TEMPLATE,
}

export function templateForReviewType(reviewType: string): ReviewTemplate {
   return TEMPLATES[reviewType] ?? DVM_REVIEW_TEMPLATE
}

/** Matches getRevieweeLabel() on the review page. */
export function revieweeRoleLabel(reviewType: string): string {
   switch (reviewType) {
      case 'HM':
         return 'Hospital Manager'
      case 'COS':
         return 'Chief of Staff'
      case 'RM':
         return 'Regional Manager'
      case 'DO':
         return 'Director of Operations'
      case 'RECEPTION':
         return 'Reception Team Member'
      case 'CROSSTRAINED':
         return 'Cross-Trained Team Member'
      case 'CLINICAL':
         return 'Clinical Team Member'
      case 'DVM':
      default:
         return 'Associate DVM'
   }
}

/** The five ratings used for Additional Performance Review Criteria. */
export const PERFORMANCE_OPTIONS: ReviewTemplateOption[] = [
   { label: 'Sets the Standard for Others', value: 'sets_the_standard_for_others' },
   { label: 'Successful', value: 'successful' },
   { label: 'Opportunity for Improvement', value: 'opportunity_for_improvement' },
   { label: 'Not Successful', value: 'not_successful' },
   { label: 'Not Applicable', value: 'not_applicable' },
]

/**
 * Same mapping the review page does in getUserReviewsForReviewee(), so question ids
 * (additional_responsibility_<id>) line up between the peer's copy and the manager's review.
 */
export function mapAdditionalResponsibilities(rows: any[], revieweeUserId: number, reviewType: string): any[] {
   const savedRow = (rows ?? [])[0]
   let texts: string[] = []

   if (savedRow?.responsibilityText) {
      try {
         const parsed = JSON.parse(savedRow.responsibilityText)
         texts = Array.isArray(parsed) ? parsed : [savedRow.responsibilityText]
      } catch {
         texts = [savedRow.responsibilityText]
      }
   }

   return texts.map((text, index) => ({
      id: savedRow?.id ? `${savedRow.id}_${index + 1}` : index + 1,
      revieweeUserId: savedRow?.revieweeUserId ?? revieweeUserId,
      reviewType: savedRow?.reviewType ?? reviewType,
      responsibilityText: text,
      displayOrder: index + 1,
   }))
}

/** Checkbox and table answers are stored as JSON arrays; everything else is a plain string. */
export function parseResponseValue(value: any): any {
   if (typeof value === 'string' && value.trim().startsWith('[')) {
      try {
         return JSON.parse(value)
      } catch {
         return value
      }
   }
   return value
}

const snakeToCamel = (id: string) => id.replace(/_([a-z0-9])/g, (_, c) => String(c).toUpperCase())

/** Reads the manager's current answer from the review object (reviewer props end in COS). */
export function readReviewerAnswer(review: any, questionId: string): { value: any; notes: string; otherText: string } {
   const base = snakeToCamel(questionId)
   return {
      value: review?.[`${base}COS`],
      notes: review?.[`${base}NotesCOS`] ?? '',
      otherText: review?.[`${base}OtherCOS`] ?? '',
   }
}

/** Question labels contain HTML (subtitles, asterisks); this keeps just the question text. */
export function plainLabel(html: string | undefined | null): string {
   return (html ?? '')
      .replace(/<span class="review-subtitle">[\s\S]*?<\/span>/g, ' ')
      .replace(/<span class="required-asterisk">[\s\S]*?<\/span>/g, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim()
}

/** Readable text for one answer (rating label, checked items, table rows, written text, notes). */
export function formatAnswerText(
   q: ReviewTemplateQuestion | null,
   rawValue: any,
   notes?: string | null,
   otherText?: string | null,
   fallbackOptions: ReviewTemplateOption[] = []
): string {
   const value = parseResponseValue(rawValue)
   const options = q ? [...(q.reviewerOptions ?? []), ...(q.options ?? [])] : fallbackOptions
   const labelOf = (v: any) => options.find((o) => String(o.value) === String(v))?.label ?? String(v)
   const parts: string[] = []

   if (q?.type === 'table') {
      const rows = (Array.isArray(value) ? value : []).filter((row) =>
         Object.values(row ?? {}).some((cell) => String(cell ?? '').trim() !== '')
      )
      rows.forEach((row, i) => {
         const cells = (q.tableColumns ?? [])
            .map((col) => {
               let cell = row?.[col.field]
               if (cell == null || String(cell).trim() === '') return null
               if (col.type === 'select') cell = col.options?.find((o) => String(o.value) === String(cell))?.label ?? cell
               if (col.type === 'date') cell = new Date(cell).toLocaleDateString()
               return `${col.label}: ${cell}`
            })
            .filter(Boolean)
         parts.push(`${i + 1}. ${cells.join(' · ')}`)
      })
   } else if (Array.isArray(value)) {
      const labels = value.filter((v) => v !== '__other__').map(labelOf)
      if (value.includes('__other__')) labels.push(otherText?.trim() ? `Other: ${otherText.trim()}` : 'Other')
      if (labels.length) parts.push(labels.join(', '))
   } else if (value != null && String(value).trim() !== '') {
      parts.push(q?.type === 'textarea' ? String(value) : labelOf(value))
   }

   if (notes?.trim()) parts.push(`Notes: ${notes.trim()}`)

   return parts.join('\n')
}

export function initials(name: string | null | undefined): string {
   return (name ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join('')
}

export function apiBase(userReviewClient: any): string {
   return (userReviewClient as any)?.baseUrl ?? ''
}
